import {Component, Inject, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, NgForm, Validators} from "@angular/forms";
import {Angular2Multiselects} from "../../../../../util/multiselect";
import {UiConstants} from "../../../../../util/core-utils";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {
  FieldValidationError,
  ForwardingNgFormRef,
  LocalFormGroupValidationErrors
} from "../../../../../lib/util/services";
import {TaskRecordCustomStatisticsModel} from "../task-record-custom-statistics.model";
import {AppValidators} from "../../../../../util/app-validators";
import {CustomStatistics} from "../../../../../lib/custom-statistics/custom-statistics";
import {
  TaskCustomStatistics,
  TaskCustomStatisticsService
} from "../../../../../lib/task/custom-statistics/task-custom-statistics.service";
import {StringKey} from "../../../../../app.string-keys";
import {ToasterService} from "../../../../../fork/angular2-toaster/src/toaster.service";
import {TranslateService} from "@ngx-translate/core";
import {IdentityMessage} from "../../../../../lib/util/messages";
import {Observable} from "rxjs";

@Component({
  selector: 'app-task-record-custom-statistics-save-dialog',
  templateUrl: './task-record-custom-statistics-save-dialog.component.html',
  styleUrls: ['./task-record-custom-statistics-save-dialog.component.scss']
})
export class TaskRecordCustomStatisticsSaveDialogComponent implements OnInit {

  UiConstants = UiConstants;

  TaskCustomStatistics = TaskCustomStatistics;

  @ViewChild('f') form: NgForm;

  formGroup: FormGroup;

  dropDownSettings = Angular2Multiselects.LOCAL_SINGLE_SELECT;

  creationInProgress: boolean = false;
  private formGroupValidationErrors: LocalFormGroupValidationErrors;
  private fieldErrors: FieldValidationError<TaskCustomStatistics.ValidatedField> =
    FieldValidationError.empty<TaskCustomStatistics.ValidatedField>();


  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private taskCustomStatisticsService: TaskCustomStatisticsService,
    private toasterService: ToasterService,
    private translateService: TranslateService,
    public dialogRef: MatDialogRef<TaskRecordCustomStatisticsSaveDialogComponent, TaskRecordCustomStatisticsSaveDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: TaskRecordCustomStatisticsSaveDialogData
  ) {
    this.formGroup = this.createFormGroup(formBuilder);
    this.formGroupValidationErrors = LocalFormGroupValidationErrors.ofForm(
      this.createForwardingHtmlForm(),
      this.formGroup
    );

  }

  ngOnInit(): void {
  }

  public static openDialog(
    dialog: MatDialog,
    data: TaskRecordCustomStatisticsSaveDialogData,
    resultCallback: (result?: TaskRecordCustomStatisticsSaveDialogResult) => void) {
    const dialogRef = dialog.open(TaskRecordCustomStatisticsSaveDialogComponent, {
      closeOnNavigation: true,
      data: data,
      width: '60%',
      height: 'auto',
      panelClass: 'custom-dialog-container'
    });
    dialogRef.afterClosed().subscribe((result: TaskRecordCustomStatisticsSaveDialogResult) => {
      resultCallback(result);
    });
  }

  private createForwardingHtmlForm() {
    return new ForwardingNgFormRef({
      formFn: () => {
        return this.form;
      }
    });
  }

  hasLocalFieldError(formControlName?: string, errorCode?: string): boolean {
    return this.formGroupValidationErrors.hasFieldError(formControlName, errorCode);
  }

  hasFieldError(field?: TaskCustomStatistics.ValidatedField): boolean {
    return this.fieldErrors.hasError(field);
  }

  removeFieldError(field: TaskCustomStatistics.ValidatedField) {
    this.fieldErrors = this.fieldErrors.removeError(field);
  }

  getFieldErrorText(field: TaskCustomStatistics.ValidatedField): string {
    return this.fieldErrors.getErrorText(field);
  }


  private createFormGroup(fb: FormBuilder): FormGroup {
    return fb.group({
      name: [
        [],
        Validators.required
      ],
      externalId: [
        [],
        AppValidators.tempValidator({
          validator: Validators.required,
          disabled: () => !this.data.model.updateInProgress
        })
      ],
    });
  }

  closeDialog(success: boolean, statId?: number) {
    this.dialogRef.close({success: success, statId: statId});
  }


  saveStat() {
    this.creationInProgress = true;
    const model = this.data.model;
    let obs: Observable<IdentityMessage>;
    if (model.createNew) {
      obs = this.taskCustomStatisticsService.create({
        taskId: this.data.taskId,
        dqlFilter: model.query,
        name: model.name,
        externalId: model.externalId,
        diagrams: model.diagramModels.map((d) => {
          return {
            formFieldData: {formFieldId: d.formFieldId},
            visualType: CustomStatistics.DiagramVisualType.PIE_CHART,
            type: CustomStatistics.DiagramType.FORM_FIELD
          }
        }),
      });
    } else {
      obs = this.taskCustomStatisticsService.update({
        id: model.selectedStatistics!.id,
        taskId: this.data.taskId,
        dqlFilter: model.query,
        name: model.name,
        externalId: model.externalId,
        diagrams: model.diagramModels.map((d) => {
          return {
            formFieldData: {formFieldId: d.formFieldId},
            visualType: CustomStatistics.DiagramVisualType.PIE_CHART,
            type: CustomStatistics.DiagramType.FORM_FIELD,
          }
        }),
      })
    }

    obs.subscribe(result => {
      this.creationInProgress = false;
      this.closeDialog(true, result.id);
    }, error => {
      this.creationInProgress = false;
      this.toasterService.pop({
        timeout: UiConstants.ToastTimeoutLong,
        type: UiConstants.toastTypeError,
        title: this.translateService.instant(StringKey.COMMON_ERROR_DIALOG_TITLE),
        body: this.translateService.instant(StringKey.COMMON_ERROR_DIALOG_MESSAGE)
      });
      if (error instanceof FieldValidationError) {
        this.fieldErrors = error;
      }
    });

  }

}

export interface TaskRecordCustomStatisticsSaveDialogResult {
  success: boolean;
  statId?: number;
}

export interface TaskRecordCustomStatisticsSaveDialogData {
  model: TaskRecordCustomStatisticsModel;
  taskId: number;
}
